import React, { useState } from "react";
import {
	Box,
	Grid,
	Typography,
	TextField,
	Button,
	Alert,
	CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";
import ArrearsBreakdownTable from "components/Tables/ArrearsBreakdownTable";
import { useArrearsBreakdown } from "services/hooks/rentals";
import { currencyFormatter } from "utilities/currencyFormatter";

const TitleContainer = styled(Grid)(({ theme }) => ({
	marginBottom: theme.spacing(2),
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
}));

export default function Arrears() {
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(20);
	const [searchInput, setSearchInput] = useState("");
	const [appliedSearch, setAppliedSearch] = useState("");

	const { rows, summary, pagination, loading, error } = useArrearsBreakdown({
		page: page + 1,
		pageSize: rowsPerPage,
		search: appliedSearch,
	});

	const applySearch = () => {
		setAppliedSearch(searchInput.trim());
		setPage(0);
	};

	const handleSearchKeyDown = (event) => {
		if (event.key === "Enter") {
			event.preventDefault();
			applySearch();
		}
	};

	const handleClearSearch = () => {
		setSearchInput("");
		setAppliedSearch("");
		setPage(0);
	};

	const handleChangePage = (_event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	return (
		<Box m="20px">
			<Grid container spacing={2} style={{ marginTop: "20px" }}>
				<Grid item xs={12}>
					<TitleContainer>
						<Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "25px" }}>
							Total Arrears
						</Typography>
					</TitleContainer>
				</Grid>
			</Grid>

			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				{currencyFormatter(summary.total_outstanding || 0)} outstanding across{" "}
				{summary.tenants_in_arrears || 0} tenant
				{summary.tenants_in_arrears === 1 ? "" : "s"}, largest first
				{summary.as_of_date ? ` — as of ${moment(summary.as_of_date).format("DD MMM YYYY")}.` : "."}
				{appliedSearch
					? ` Matching "${appliedSearch}": ${currencyFormatter(summary.filtered_outstanding || 0)}.`
					: ""}
			</Typography>

			<Box display="flex" gap={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
				<TextField
					size="small"
					label="Search by tenant, RB reference, unit or property"
					value={searchInput}
					onChange={(e) => setSearchInput(e.target.value)}
					onKeyDown={handleSearchKeyDown}
					sx={{ minWidth: 260, flex: "1 1 240px", maxWidth: 420 }}
				/>
				<Button variant="contained" startIcon={<SearchIcon />} onClick={applySearch}>
					Search
				</Button>
				{appliedSearch ? (
					<Button variant="text" onClick={handleClearSearch}>
						Clear
					</Button>
				) : null}
			</Box>

			{loading && (
				<Box display="flex" justifyContent="center" py={4}>
					<CircularProgress />
				</Box>
			)}

			{error && !loading && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{typeof error === "string" ? error : "Error loading arrears."}
				</Alert>
			)}

			{!loading && !error && (
				<ArrearsBreakdownTable
					rows={rows}
					count={pagination.total ?? 0}
					page={page}
					rowsPerPage={rowsPerPage}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			)}
		</Box>
	);
}
